import React, { useEffect, useState } from "react";
import { Tag, Spin, Table, Typography, Tooltip } from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    BlockOutlined,
} from "@ant-design/icons";
import blockchainApi from "../../api/blockchainApi";

const { Text } = Typography;

const STATUS_MAP = {
    pending_fee: { color: "orange", icon: <ClockCircleOutlined />, label: "Pending Fee" },
    pending_deposit: { color: "blue", icon: <ClockCircleOutlined />, label: "Pending Deposit" },
    funded: { color: "green", icon: <CheckCircleOutlined />, label: "Funded" },
    claiming: { color: "cyan", icon: <CheckCircleOutlined />, label: "Claiming" },
    completed: { color: "purple", icon: <CheckCircleOutlined />, label: "Completed" },
    withdrawn: { color: "default", icon: <CloseCircleOutlined />, label: "Withdrawn" },
};

function truncateAddress(addr) {
    if (!addr) return "—";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

const claimColumns = [
    {
        title: "Winner",
        dataIndex: "winnerAddress",
        key: "winnerAddress",
        render: (v) => (
            <Tooltip title={v}>
                <Text copyable={{ text: v }} className="text-xs">
                    {truncateAddress(v)}
                </Text>
            </Tooltip>
        ),
    },
    {
        title: "Amount",
        dataIndex: "amountHuman",
        key: "amountHuman",
        render: (v, row) => `${v} ${row.tokenSymbol || ""}`,
    },
    {
        title: "Tx",
        dataIndex: "txHash",
        key: "txHash",
        render: (v) => (
            <Tooltip title={v}>
                <Text copyable={{ text: v }} className="text-xs">
                    {truncateAddress(v)}
                </Text>
            </Tooltip>
        ),
    },
    {
        title: "Block",
        dataIndex: "blockNumber",
        key: "blockNumber",
    },
];

export default function PromoBlockchainInfo({ promoId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!promoId) return;
        setLoading(true);
        blockchainApi
            .getPromoBlockchainInfo(promoId)
            .then((res) => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [promoId]);

    if (loading) return <Spin size="small" className="mt-2 [&_.ant-spin-dot-item]:!bg-emerald-400" />;
    if (!data || !data.blockchainStatus) return null;

    const st = STATUS_MAP[data.blockchainStatus] || {
        color: "default",
        icon: null,
        label: data.blockchainStatus,
    };

    return (
        <div className="mt-2 flex w-full flex-col gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur-xl">
            <div className="flex items-center gap-2">
                <BlockOutlined className="text-purple-400" />
                <span className="text-white text-sm font-semibold">Blockchain</span>
                <Tag icon={st.icon} color={st.color}>
                    {st.label}
                </Tag>
            </div>

            <div className="flex flex-col gap-1 text-xs text-white/45">
                <div className="flex justify-between">
                    <span>Chain ID</span>
                    <span className="text-white">{data.chainId || "—"}</span>
                </div>

                <div className="flex justify-between">
                    <span>Promoter</span>
                    <Tooltip title={data.promoterAddress}>
                        <Text
                            copyable={data.promoterAddress ? { text: data.promoterAddress } : false}
                            className="text-white text-xs"
                        >
                            {truncateAddress(data.promoterAddress)}
                        </Text>
                    </Tooltip>
                </div>

                <div className="flex justify-between">
                    <span>Token</span>
                    <Tooltip title={data.tokenContractAddress}>
                        <Text
                            copyable={data.tokenContractAddress ? { text: data.tokenContractAddress } : false}
                            className="text-white text-xs"
                        >
                            {truncateAddress(data.tokenContractAddress)}
                        </Text>
                    </Tooltip>
                </div>

                <div className="flex justify-between items-center">
                    <span>Fee Tx</span>
                    {data.feeTxHash ? (
                        <Tooltip title={data.feeTxHash}>
                            <Tag icon={<CheckCircleOutlined />} color="green" className="m-0">
                                Paid
                            </Tag>
                        </Tooltip>
                    ) : (
                        <Tag icon={<ClockCircleOutlined />} color="orange" className="m-0">
                            Pending
                        </Tag>
                    )}
                </div>

                <div className="flex justify-between items-center">
                    <span>Deposit Tx</span>
                    {data.depositTxHash ? (
                        <Tooltip title={data.depositTxHash}>
                            <Tag icon={<CheckCircleOutlined />} color="green" className="m-0">
                                Deposited
                            </Tag>
                        </Tooltip>
                    ) : (
                        <Tag icon={<ClockCircleOutlined />} color="orange" className="m-0">
                            Pending
                        </Tag>
                    )}
                </div>
            </div>

            {data.contracts && (
                <div className="mt-1 flex flex-col gap-0.5 text-[10px] text-white/40">
                    <Tooltip title={data.contracts.giveawayPool}>
                        <span>GiveawayPool: {truncateAddress(data.contracts.giveawayPool)}</span>
                    </Tooltip>
                    <Tooltip title={data.contracts.feeCollector}>
                        <span>FeeCollector: {truncateAddress(data.contracts.feeCollector)}</span>
                    </Tooltip>
                </div>
            )}

            {data.claims && data.claims.length > 0 && (
                <div className="mt-2">
                    <span className="text-white text-xs font-semibold">
                        Claims ({data.claims.length})
                    </span>
                    <Table
                        dataSource={data.claims}
                        columns={claimColumns}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        className="mt-1"
                    />
                </div>
            )}
        </div>
    );
}
